import {useEffect, useState} from 'react'

import {useForm, useFormState} from 'react-final-form'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {useParams} from 'react-router-dom'

import {DeepEqualityField} from '../../../Components/Common/Form/DeepEqualityField'
import BaseCheckBox from '../../../Components/Common/Inputs/CheckBox'
import {NumberInput} from '../../../Components/Common/Inputs/NumberInput/NumberInput'
import {incrementAlphabetical} from '../components/article-details-form/utils/incrementAlphabetical'
import {MeasureSelectInpuit} from '../components/article-details-tabs/measure-tab/measurements-table/components/MeasureSelectInput'

const createMeasurementsTableConfig = (currentFieldName, isLoadingInfo, t) => ({
  columns: [
    {
      type: 'custom',
      field: 'letter',
      align: 'flex-start',
      header: t('LETTER'),
      body: data => {
        return data.letter.toLocaleUpperCase()
      }
    },
    {
      field: 'measure',
      header: t('MEASURE'),
      align: 'flex-start',
      type: 'custom',
      body: (data, rowIndex) => {
        return (
          <div className="measure-column">
            <DeepEqualityField
              name={`${currentFieldName}[${rowIndex}].measureid`}
              type="select">
              {({input}) => {
                return (
                  <MeasureSelectInpuit isLoading={isLoadingInfo} {...input} />
                )
              }}
            </DeepEqualityField>
          </div>
        )
      }
    },
    {
      field: 'value',
      header: t('VALUE'),
      align: 'center',
      type: 'custom',
      body: (data, rowIndex) => {
        return (
          <div className="d-flex justify-content-center">
            <DeepEqualityField name={`${currentFieldName}[${rowIndex}].value`}>
              {({input}) => {
                return (
                  <div className="text-center measurement-value-input-container">
                    <NumberInput {...input} />
                  </div>
                )
              }}
            </DeepEqualityField>
          </div>
        )
      }
    },
    {
      field: 'adjustable',
      header: t('ADJUSTABLE'),
      type: 'custom',
      align: 'center',
      body: (_, rowIndex) => {
        return (
          <div className="d-flex justify-content-center">
            <DeepEqualityField
              name={`${currentFieldName}[${rowIndex}].adjustable`}
              type="checkbox">
              {({input}) => {
                return <BaseCheckBox {...input} value={input.checked} />
              }}
            </DeepEqualityField>
          </div>
        )
      }
    }
  ],
  footer: false,
  scroll: {
    scrollable: false
  },
  editrow: {
    hasEdit: false
  }
})

const currentCombinationFieldName = 'measuresSection.measurements.measures'

export const useMeasurementsTable = () => {
  const formValues = useFormState()?.values || {}
  const form = useForm()
  const {isLoading: isLoadingInfo} = useSelector(state => state.Info)
  const {t} = useTranslation()
  const {productId} = useParams()

  const measuresSection = formValues.measuresSection || {}

  const {sizes = [], widths = []} = measuresSection || {}

  const {measures = []} = measuresSection.measurements || {}

  const [sizeSelectValue, setSizeSelectValue] = useState(sizes[0]?.sizeId)
  const [widthSelectValue, setWidthSelectValue] = useState(
    widths[0]?.productWidthId
  )
  const [currentMeasuresIndex, setCurrentMeasuresIndex] = useState(0)
  const currentFieldName = `measuresSection.measurements.measures[${currentMeasuresIndex}].measuresArray`
  const currentSelectedCombinationFieldName = `measuresSection.measurements.measures[${currentMeasuresIndex}]`
  const currentCombination =
    formValues?.measuresSection?.measurements?.measures?.[currentMeasuresIndex]

  const currentMeasure = currentCombination?.measuresArray

  const updateCurrentMeasures = (sizeId, productWidthId) => {
    const combination = measures.findIndex(measure => {
      return (
        measure.sizeId === sizeId && measure.productWidthId === productWidthId
      )
    })

    if (combination !== -1) {
      setCurrentMeasuresIndex(combination)
    } else {
      setCurrentMeasuresIndex(measures.length)
    }
  }

  useEffect(() => {
    setSizeSelectValue(sizes[0]?.sizeId)
    setWidthSelectValue(widths[0]?.productWidthId)
  }, [sizes[0]?.sizeId, widths[0]?.productWidthId])

  const handleSizeSelectChange = value => {
    setSizeSelectValue(value)
    updateCurrentMeasures(value, widthSelectValue)
  }

  const handleWidthSelectChange = value => {
    setWidthSelectValue(value)
    updateCurrentMeasures(sizeSelectValue, value)
  }

  const handleAddLineClick = () => {
    let nextLetter = 'a'
    if (currentMeasure?.length) {
      const lastLetter = currentMeasure[currentMeasure?.length - 1]?.letter
      if (lastLetter) {
        nextLetter = incrementAlphabetical(lastLetter)
      }
    }

    const combinationInfo = {
      productId: +productId,
      productWidthId: widthSelectValue,
      sizeId: sizeSelectValue
    }

    if (!currentCombination) {
      form.mutators.push(currentCombinationFieldName, {
        ...combinationInfo,
        measuresArray: [{letter: nextLetter}]
      })
    } else {
      form.mutators.push(currentFieldName, {letter: nextLetter})
    }
  }

  const handleRemoveLineClick = () => {
    if (currentMeasure && currentMeasure.length > 0) {
      form.mutators.pop(currentFieldName)
    }
  }

  return {
    sizesList: sizes,
    widthsList: widths,
    measurementsTableConfig: createMeasurementsTableConfig(
      currentFieldName,
      isLoadingInfo,
      t
    ),
    sizeSelectConfig: {
      value: sizeSelectValue,
      onchange: handleSizeSelectChange
    },
    widthSelectConfig: {
      value: widthSelectValue,
      onchange: handleWidthSelectChange
    },
    handleAddLineClick,
    handleRemoveLineClick,
    currentFieldName,
    currentSelectedCombinationFieldName,
    canRemoveMeasures: currentMeasure?.length > 0
  }
}
